/**
 * Envision customizations
 * assets/cart-compatibility-checker.js
 *
 * Handles incompatible products in both cart drawer and main cart
 */

class CartCompatibilityChecker {
  /**
   * @param {Object} options
   * @param {string} options.contextSelector - CSS selector for the cart context (e.g., 'cart-drawer', 'form#cart')
   * @param {string} options.checkoutButtonSelector - CSS selector for the checkout button in this context
   */
  constructor({ contextSelector, checkoutButtonSelector }) {
    this.contextSelector = contextSelector;
    this.checkoutButtonSelector = checkoutButtonSelector;
    this.incompatibleProducts = this.getIncompatibleProducts();
    this.warningSection = null;
    this.warningText = null;
    this.checkoutButton = null;
    this.init();
  }

  getIncompatibleProducts() {
    try {
      return window.themeSettings?.incompatibleProducts || [];
    } catch (error) {
      console.warn('Cart Compatibility: Unable to load incompatible products from settings');
      return [];
    }
  }

  init() {
    if (this.incompatibleProducts.length === 0) return;
    this.bindEvents();
    this.checkCartCompatibility();
  }

  findDOMElements() {
    const context = document.querySelector(this.contextSelector);
    if (!context) return;
    this.warningSection = context.querySelector('.cart_warning_section');
    this.warningText = context.querySelector('.cart_warning_text');
    this.checkoutButton = context.querySelector(this.checkoutButtonSelector);
  }

  bindEvents() {
    document.addEventListener('on:cart:add', () => {
      setTimeout(() => this.checkCartCompatibility(), 100);
    });
    document.addEventListener('on:line-item:change', () => {
      setTimeout(() => {
        this.checkCartCompatibility();
      }, 300);
    });
    if (this.contextSelector === 'cart-drawer') {
      document.addEventListener('on:cart-drawer:after-open', () => {
        this.checkCartCompatibility();
      });
    }
    document.addEventListener('on:cart:render', () => {
      this.checkCartCompatibility();
    });
    if (this.warningSection) {
      this.setupWarningToggle();
    }
  }

  setupWarningToggle() {
    const warningIcon = this.warningSection.querySelector('.cart_warning_toggle');
    if (warningIcon && this.warningText) {
      warningIcon.setAttribute('aria-controls', 'cart-warning-details');
      this.warningText.setAttribute('id', 'cart-warning-details');
      this.warningText.setAttribute('aria-label', 'Cart warning text');
      warningIcon.setAttribute('id', 'cart-warning-toggle');
      const toggleWarning = () => {
        const isExpanded = this.warningText.classList.contains('expanded');
        if (isExpanded) {
          this.warningText.classList.remove('expanded');
          this.warningText.setAttribute('aria-hidden', 'true');
          warningIcon.classList.remove('expanded');
          warningIcon.setAttribute('aria-expanded', 'false');
          const textSpan = warningIcon.querySelector('.toggle-text');
          if (textSpan) textSpan.textContent = 'More info';
        } else {
          this.warningText.classList.add('expanded');
          this.warningText.setAttribute('aria-hidden', 'false');  
          warningIcon.classList.add('expanded');
          warningIcon.setAttribute('aria-expanded', 'true');
          const textSpan = warningIcon.querySelector('.toggle-text');
          if (textSpan) textSpan.textContent = 'Less info';
        }
      };
      warningIcon.onclick = toggleWarning;
      this.warningText.classList.remove('expanded');
      warningIcon.classList.remove('expanded');
      warningIcon.setAttribute('aria-expanded', 'false');
    }
  }

  getCurrentCartItems() {
    // Try multiple selectors to be robust
    let cartItems = [];
    const context = document.querySelector(this.contextSelector);
    if (context) {
      cartItems = context.querySelectorAll('tr.cart-item[data-product-id], [data-product-id].cart-item');
    }
    const items = Array.from(cartItems).map(item => ({
      productId: parseInt(item.dataset.productId),
      element: item
    })).filter(item => !isNaN(item.productId));
    return items;
  }

  checkCartCompatibility() {
    this.findDOMElements();
    const cartItems = this.getCurrentCartItems();
    if (cartItems.length <= 1) {
      this.hideWarning();
      return;
    }
    const incompatibleInCart = cartItems.filter(item =>
      this.incompatibleProducts.some(incompatibleProduct =>
        incompatibleProduct.id === item.productId
      )
    );
    if (incompatibleInCart.length === 0) {
      this.hideWarning();
      return;
    }
    const hasOtherProducts = cartItems.length > incompatibleInCart.length;
    if (hasOtherProducts) {
      this.showWarning(incompatibleInCart);
    } else {
      this.hideWarning();
    }
  }

  showWarning(incompatibleProducts) {
    if (!this.warningSection) {
      this.findDOMElements();
    }
    if (!this.warningSection) return;
    const firstIncompatible = incompatibleProducts[0];
    const incompatibleProduct = this.incompatibleProducts.find(p => p.id === firstIncompatible.productId);
    if (!incompatibleProduct) return;
    this.updateWarningContent(incompatibleProduct);
    this.warningSection.style.display = '';
    this.disableCheckoutButton();
    this.setupWarningToggle();
  }

  disableCheckoutButton() {
    if (this.checkoutButton) {
      this.checkoutButton.setAttribute('disabled', 'true');
      this.checkoutButton.style.pointerEvents = 'none';
    }
  }

  enableCheckoutButton() {
    if (this.checkoutButton) {
      this.checkoutButton.removeAttribute('disabled');
      this.checkoutButton.style.pointerEvents = '';
    }
  }

  updateWarningContent(incompatibleProduct) {
    const messageTemplate = window.themeSettings?.incompatibleProductsMessageTemplate ||
      '{{product_title}} CANNOT SHIP WITH OTHER SPICES';
    const instructionsTemplate = window.themeSettings?.incompatibleProductsInstructions ||
      '<p>Please remove {{product_title}} from your cart and proceed to checkout. Then, place a separate order for {{product_title}}.</p>';
    const productTitle = incompatibleProduct.title || 'This product';
    const warningMessage = messageTemplate.replace(/\{\{product_title\}\}/g, productTitle);
    const warningInstructions = instructionsTemplate.replace(/\{\{product_title\}\}/g, productTitle);
    const headingElement = this.warningSection.querySelector('.cart_warning_heading_text');
    if (headingElement) {
      const iconElement = headingElement.querySelector('.cart_warning_icon');
      const iconHTML = iconElement ? iconElement.outerHTML : '';
      headingElement.innerHTML = iconHTML + warningMessage;
    }
    if (this.warningText) {
      this.warningText.innerHTML = warningInstructions;
    }
  }

  hideWarning() {
    if (this.warningSection) {
      this.warningSection.style.display = 'none';
    }
    this.enableCheckoutButton();
  }
}

// Export for use in other scripts
window.CartCompatibilityChecker = CartCompatibilityChecker; 